import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { HttpBaseService } from '../../../core';
import { App, DismissedSiteDataPreviewConfigurationMessage, EmailNotificationCadence, Project, User, UserPhoneRequest as UserCellphoneRequest, UserEmailRequest } from '../../models';
import { environment } from './../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
/* istanbul ignore file */
export class UsersDataService extends HttpBaseService {
    private route = environment.api + 'users/';

    constructor(
        private _httpClient: HttpClient,
    ) {
        super(_httpClient);
    }

    updateAppAdminStatus(app: App, user: User): Observable<User> {
        const url = `${this.route}${user.id}/apps/${app.id}/admin`;
        return this.httpPut(url, user.isAppAdmin);
    }

    updateProjectAdminStatus(app: App, project: Project, user: User): Observable<User> {
        const url = `${this.route}${user.id}/apps/${app.id}/projects/${project.id}/admin`;
        return this.httpPut(url, user.isProjectAdmin);
    }

    getAppAdminsByAppId(app: App): Observable<User[]> {
        const url = `${this.route}apps/${app.id}/admins`;
        return this.httpGet(url);
    }

    removeUserFromApp(user: User): Observable<any> {
        const url = `${this.route}${user.id}/apps/${user.appId}`;
        return this.httpPut(url, user);
    }

    updatePlatformUser(user: User): Observable<User> {
        const url = `${this.route}platform/${user.id}`;
        return this.httpPut(url, user);
    }

    isAccountApprover(user: User): Observable<User> {
        const url = `${this.route}platform/${user.id}/apps/isAccountApprover`;
        return this.httpPut(url, user);
    }

    changeActive(user: User): Observable<any> {
        const url = `${this.route}platform/${user.id}/apps/active`;
        return this.httpPut(url, user);
    }

    editPlatformUserProperties(user: User): Observable<any> {
        const url = `${this.route}platform/${user.id}/apps/firstName,lastName,email,cellPhone`;
        return this.httpPut(url, user);
    }

    getUser(userId: string): Observable<User> {
        const url = `${this.route}${userId}`;
        return this.httpGet(url);
    }

    loadUsersByApplicationAndProject(app: App, project: Project): Observable<User[]> {
        const url = `${this.route}apps/${app.id}/projects/${project.id}`;
        return this.httpGet(url);
    }

    getUsersByApp(app: App): Observable<User[]> {
        const url = `${this.route}apps/${app.id}`;
        return this.httpGet(url);
    }

    getPlatformUsers(): Observable<any> {
        const url = `${this.route}platform`;
        return this.httpGet(url);
    }

    createPlatformUser(user: User): Observable<any> {
        const url = `${this.route}platform`;
        return this.httpPost(url, user);
    }

    createPlatformUserAndAddToApp(user: User): Observable<any> {
        const url = `${this.route}platform/apps/${user.appId}`;
        return this.httpPost(url, user);
    }

    addUserToApp(user: User): Observable<HttpErrorResponse | User> {
        const url = `${this.route}apps/${user.appId}`;
        return this.httpPost(url, user);
    }

    setProjectUserAccess(user: User): Observable<User> {
        const url = `${this.route}${user.id}/apps/${user.appId}/projects/${user.projId}/access`;
        return this.httpPut(url, user);
    }

    appsAndProjectsByUserId(user: User): Observable<User> {
        const url = `${this.route}platform/${user.id}/apps/projects`;
        return this.httpGet(url);
    }

    getAppUser(app: App, user: User): Observable<User> {
        const url = `${this.route}${user.id}/apps/${app.id}`;
        return this.httpGet(url);
    }

    updateUserRole(app: App, user: User): Observable<User> {
        const url = `${this.route}${user.id}/apps/${app.id}/roles`;
        return this.httpPut(url, user);
    }

    updateUserAccountSettings(user: User): Observable<User> {
        const url = `${this.route}platform/${user.id}/apps/firstName,lastName,cellPhone`;
        return this.httpPut(url, user);
    }

    addUserEmailNotificationCadenceToPlatformUser(user: User, emailNotificationCadence: EmailNotificationCadence): Observable<User> {
        const url = `${this.route}platform/${user.id}/email-notification-cadence`;
        return this.httpPut(url, emailNotificationCadence);
    }

    updateUserSitesAccess(user: User, app: App, project: Project): Observable<User> {
        const url = `${this.route}${user.id}/apps/${app.id}/projects/${project.id}/sites-access`;
        return this.httpPut(url, user);
    }

    updatePlatformUserReleaseSummaryCloseDate(userId: string, releaseSummaryClosedDate: string): Observable<User> {
        const url = `${this.route}platform/${userId}/release-summary-closed-date`;
        return this.httpPut(url, { closedDate: releaseSummaryClosedDate });
    }

    dismissMapSiteDataPreviewConfigurationMessage(currentUser: User, dismissedSiteDataPreviewConfigurationMessage: DismissedSiteDataPreviewConfigurationMessage, app: App): Observable<User> {
        const url = `${this.route}${currentUser.id}/apps/${app.id}/dismiss-site-data-preview-configuration-message`;
        return this.httpPut(url, dismissedSiteDataPreviewConfigurationMessage);
    }

    userEmailExist(userEmailRequest: UserEmailRequest): Observable<boolean> {
        const url = `${this.route}user-email`;
        return this.httpPost(url, userEmailRequest);
    }

    userPhoneNumberExist(userPhoneRequest: UserCellphoneRequest): Observable<boolean> {
        const url = `${this.route}user-cellphone`;
        return this.httpPost(url, userPhoneRequest);
    }

    removeAppAdmin(app: App, user: User): Observable<User> {
        const url = `${this.route}${app.id}/users/${user.id}/admin`;
        return this.httpDelete(url);
    }

    addAppAdmin(app: App, user: User): Observable<User> {
        const url = `${this.route}${app.id}/users/${user.id}/admin`;
        return this.httpPost(url, user);
    }
}
